
import { mapGetters } from "vuex"
import pageMixin from "@/mixins/pageMixin"
import redirectMixin from "@/mixins/redirectMixin"
import GetTariffs from "@/services/GetTariffs.js"
import QuoteInput from "@soenergy/frontend-library/src/components/QuoteInput"
import Article from "@soenergy/frontend-library/src/components/Article"
import AccordionCard from "@soenergy/frontend-library/src/components/AccordionCard"
import SimpleTable from "@/components/SimpleTable"
import InfiniteScroll from "@soenergy/frontend-library/src/components/InfiniteScroll"
import AppButton from "@soenergy/frontend-library/src/components/AppButton"
import FeedbackDialog from "@soenergy/frontend-library/src/components/FeedbackDialog"
import Checkbox from "@soenergy/frontend-library/src/components/Checkbox"
import SegmentedGroup from "@soenergy/frontend-library/src/components/SegmentedButton/SegmentedGroup"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import AppSection from "@soenergy/frontend-library/src/components/AppSection"
import ContentBlockList from "@soenergy/frontend-library/src/components/ContentBlockList"
import AccordionTransition from "@soenergy/frontend-library/src/components/AccordionTransition"
import { tariffDisplayName, isNonDD } from "../helpers/ourTariffsHelper"

// Our tariff SEO improvements

export default {
  components: {
    AccordionTransition,
    QuoteInput,
    Checkbox,
    Article,
    AccordionCard,
    AppButton,
    FeedbackDialog,
    SegmentedGroup,
    SimpleTable,
    InfiniteScroll,
    AppSection,
    ContentBlockList
  },
  mixins: [pageMixin, redirectMixin],
  asyncData({ $storyblokHelper }) {
    return $storyblokHelper.get({ id: "our-tariffs-v2", version: "2" })
  },
  data() {
    return {
      errorMessage: "",
      postcode: "",
      pageNumber: 1,
      isShowingCurrentTariffs: true,
      isPostcodeSearchCompleted: false,
      filters: {
        current: true,
        variable: false,
        fixed: false,
        directDebit: true
      },
      tariffDisplayName,
      timePeriodOptions: [
        {
          value: true,
          label: "Current Tarriffs"
        },
        {
          value: false,
          label: "Historical Tariffs"
        }
      ],
      tariffTypeOptions: [
        {
          value: "false",
          label: "Fixed",
          inputId: "fixed-tariff"
        },
        {
          value: "true",
          label: "Variable",
          inputId: "variable-tariff"
        }
      ],
      tariffResults: {},
      tariffNames: [],
      isFetching: false,
      serverError: ""
    }
  },
  computed: {
    ...mapGetters("userStatus", ["isLoggedIn"])
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.findTariffs()
      }
    }
  },
  methods: {
    findTariffs(data) {
      this.postcode =
        data && data.customerPostcode ? data.customerPostcode : this.postcode
      this.pageNumber = data && data.requestedPage ? data.requestedPage : 1
      const apiData = {
        postcode: this.postcode,
        page: this.pageNumber,
        filters: {
          historical: !this.filters.current
        }
      }
      // selecting both or neither variable and fixed should return all results
      if (this.filters.variable && !this.filters.fixed) {
        apiData.filters.variable = true
      } else if (this.filters.fixed && !this.filters.variable) {
        apiData.filters.variable = false
      }
      // reset the page
      if (this.pageNumber === 1) {
        this.$refs.tariffInfiniteScrollList.reset()
        this.tariffResults = {}
        this.tariffNames = []
        this.serverError = ""
      }
      // new postcode search
      if (data?.customerPostcode) {
        this.isPostcodeSearchCompleted = false
      }
      // send api call here
      this.isFetching = true
      GetTariffs.fetchTariffs(apiData)
        .then(response => {
          this.isPostcodeSearchCompleted = true
          if (response && response.data && response.data.active_page) {
            // check if we ran out of results
            if (response.data.total_pages <= response.data.current_page) {
              this.$refs.tariffInfiniteScrollList.stopLoadingMorePages()
            } else {
              this.$refs.tariffInfiniteScrollList.hideSpinner()
            }
            // save data locally and filter out non DD duplication
            Object.keys(response.data.active_page).forEach((key, index) => {
              this.tariffResults[key] = response.data.active_page[key].filter(
                rate => rate.direct_debit
              )
            })
            this.tariffNames = Object.keys(this.tariffResults)
          } else {
            throw new Error("Invalid response.")
          }
        })
        .catch(error => {
          this.serverError = "There was an error: " + error.message
          ErrorReporter.report(error)
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    getTags(tariffName) {
      const tariff = this.tariffResults[tariffName]
      const tagsArray = []

      if (tariff && tariff[0]) {
        if (!tariff[0].variable) {
          tagsArray.push(tariff[0].duration_months + " month fixed")
        } else {
          tagsArray.push("variable")
        }
      }

      return tagsArray
    },
    getTariffAttrsRow(tariffName) {
      const tariff = this.tariffResults[tariffName][0]

      return [
        isNonDD(tariffName) ? "Non-Direct Debit" : "Direct Debit",
        "Online",
        tariff.early_termination_charge
          ? `£${tariff.early_termination_charge} / fuel`
          : "£0",
        "",
        ""
      ]
    },
    getTariffRateTableData(tariffName) {
      const tariff = this.tariffResults[tariffName]
      const elec2Rows = []
      const elec1Rows = []
      const gasRows = []

      if (tariff) {
        // we could have 3 tariffs(gas, elec1, elec2) to loop in order to build 1 card
        for (let i = 0; i < tariff.length; i++) {
          const isElec = tariff[i].display.includes("Electricity")
          const isGas = tariff[i].display.includes("Gas")
          // add elec 2 rate
          if (tariff[i].night_rate) {
            elec2Rows.push([
              "Eco 7 Electricity",
              "",
              tariff[i].standing_charge + "p / day",
              tariff[i].day_rate + "p / kWh",
              tariff[i].night_rate + "p / kWh"
            ])
          }
          // add elec 1 and gas rates
          else if (isElec || isGas) {
            elec1Rows.push([
              isElec ? "Electricity" : "Gas",
              tariff[i].standard_rate + "p / kWh",
              tariff[i].standing_charge + "p / day",
              "",
              ""
            ])
          }
        }
      }

      let headers = ["", "Unit Rate", "Standing charge"]
      if (elec2Rows.length) {
        headers = [...headers, "Day rate", "Night rate"]
      }

      return {
        headers,
        rows: [...elec2Rows, ...elec1Rows, ...gasRows]
      }
    }
  }
}
